import React, {useState, useMemo, memo, useCallback, useEffect} from 'react';
import {
  Box,
  Table,
  TableBody,
  TableFooter,
  TableHead,
  Typography,
  Skeleton,
  Stack,
  InputAdornment,
  Tooltip,
  Link,
} from '@mui/material';
import {debounce} from 'lodash';
import {WorkbookPageHeader} from '../WorkbookPageHeader';
import {APP_HEADER_HEIGHT} from '../../../../App';
import {
  useGetRuleMetadataQuery,
  useGetRuleUnpaidFixedAssetsQuery,
} from '../../../../data/api';
import {useLocation, useNavigate} from 'react-router-dom';
import {renderAmount, formatDateMonthYear, sortRecords} from '../../../../util';
import {LoadingUnpaidFixedAssetsTable} from './LoadingUnpaidFixedAssetsTable';
import {SearchIcon} from 'lucide-react';
import {useParamReportPeriodOrDefault} from '../../../../util/useParamReportPeriodOrDefault';
import {
  StyledBodyCell,
  StyledHeadCell,
  StyledTableRow,
  StyledFooterCell,
  StyledTableContainer,
  StyledOutlinedInput,
} from '../components';
import {filterByQuery} from '../filterByQuery';

function UnpaidFixedAssetsPage() {
  const {state = {}} = useLocation();
  const navigate = useNavigate();
  const {pliId, indirectPliId} = state;
  const period = useParamReportPeriodOrDefault();

  useEffect(() => {
    if (!pliId || !indirectPliId) {
      navigate('/statements/indirect-cashflow-workbook');
    }
  }, [pliId, indirectPliId]);

  const {data: ruleMetadata, isFetching: isFetchingMetadata} =
    useGetRuleMetadataQuery({pliId, indirectPliId, period});
  const {data: ruleUnpaidFixedAssets, isFetching: isFetchingUnpaidFixedAssets} =
    useGetRuleUnpaidFixedAssetsQuery({pliId, indirectPliId, period});

  // Manage the query string and debouncing for performance
  const [query, setQuery] = useState('');
  const debouncedSetQuery = useMemo(() => debounce(setQuery, 300), [setQuery]);
  const handleChange = useCallback(
    (e) => {
      debouncedSetQuery(e.target.value);
    },
    [debouncedSetQuery]
  );

  const filteredRuleTransactions = useMemo(() => {
    const kindMapping = {
      paid_in_current_month: 'Paid in period',
      in_period_additions: 'Unpaid',
    };

    const filtered = filterByQuery(
      (ruleUnpaidFixedAssets ?? []).map((data) => ({
        ...data,
        formattedKind: kindMapping[data.kind],
      })),
      query,
      {
        numericField: 'amount',
        textFields: ['account', 'formattedKind', 'document_number'],
      }
    );

    return sortRecords(filtered, 'desc', 'amount');
  }, [ruleUnpaidFixedAssets, query]);

  const totalAmount = useMemo(() => {
    if (!ruleUnpaidFixedAssets || ruleUnpaidFixedAssets.length === 0) {
      return 0;
    }
    return ruleUnpaidFixedAssets.reduce((sum, transaction) => {
      return sum + (transaction.amount ?? 0);
    }, 0);
  }, [ruleUnpaidFixedAssets]);

  return (
    <>
      <WorkbookPageHeader
        title={
          isFetchingMetadata ? (
            <Skeleton width="400px" sx={{display: 'inline-block'}} />
          ) : (
            ruleMetadata?.name
          )
        }
      ></WorkbookPageHeader>
      <Box
        mt={APP_HEADER_HEIGHT}
        sx={{
          flex: 1,
          overflow: 'hidden',
        }}
      >
        <Stack
          width="100%"
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          py={2}
          px={3}
        >
          <StyledOutlinedInput
            onInput={handleChange}
            startAdornment={
              <InputAdornment position="start">
                <SearchIcon size={16} />
              </InputAdornment>
            }
            placeholder="Internal ID, account, amount"
          />
          <Tooltip
            title={ruleMetadata?.classification_desc}
            enterDelay={1000}
            placement="bottom-end"
          >
            <Typography
              fontSize="12px"
              mx={3}
              my={2}
              width="100%"
              whiteSpace="nowrap"
              textAlign="right"
              textOverflow="ellipsis"
              overflow="hidden"
            >
              <Typography fontSize="12px" fontWeight="bold" component="span">
                Classification rule =
              </Typography>{' '}
              {isFetchingMetadata ? (
                <Skeleton width="400px" sx={{display: 'inline-block'}} />
              ) : (
                ruleMetadata?.classification_desc
              )}
            </Typography>
          </Tooltip>
        </Stack>
        <StyledTableContainer>
          {isFetchingUnpaidFixedAssets ? (
            <LoadingUnpaidFixedAssetsTable />
          ) : (
            <Table
              stickyHeader
              sx={{borderCollapse: 'separate', 'p, span': {fontSize: 12}}}
            >
              <TableHead>
                <StyledTableRow sx={{border: '0 !important'}}>
                  <StyledHeadCell>
                    <Typography>Internal ID</Typography>
                  </StyledHeadCell>
                  <StyledHeadCell>
                    <Typography>Account Name</Typography>
                  </StyledHeadCell>
                  <StyledHeadCell>
                    <Typography>Bill Posting Period</Typography>
                  </StyledHeadCell>
                  <StyledHeadCell>
                    <Typography>Period Closed</Typography>
                  </StyledHeadCell>
                  <StyledHeadCell>
                    <Typography>Type</Typography>
                  </StyledHeadCell>
                  <StyledHeadCell>
                    <Typography>Amount</Typography>
                  </StyledHeadCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {!isFetchingUnpaidFixedAssets &&
                  filteredRuleTransactions.length === 0 && (
                    <StyledTableRow>
                      <StyledBodyCell
                        colSpan={5}
                        sx={{
                          borderBottom: 0,
                          textAlign: 'center',
                        }}
                      >
                        <Typography sx={{fontWeight: 500}}>
                          No results found
                        </Typography>
                      </StyledBodyCell>
                    </StyledTableRow>
                  )}

                {filteredRuleTransactions.map((transaction, idx) => (
                  <StyledTableRow
                    key={`${transaction.document_number}-${idx}`}
                    sx={{
                      ':last-child > td': {
                        borderBottom: 0,
                      },
                    }}
                  >
                    <StyledBodyCell>
                      <Link
                        href={`https://system.netsuite.com/app/accounting/transactions/transaction.nl?id=${transaction.document_number}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{fontSize: '12px'}}
                      >
                        {transaction.document_number}
                      </Link>
                    </StyledBodyCell>
                    <StyledBodyCell>
                      <Typography>{transaction.account}</Typography>
                    </StyledBodyCell>
                    <StyledBodyCell>
                      <Typography>
                        {formatDateMonthYear(transaction.date)}
                      </Typography>
                    </StyledBodyCell>
                    <StyledBodyCell>
                      <Typography>
                        {formatDateMonthYear(transaction.month_of_date_closed)}
                      </Typography>
                    </StyledBodyCell>
                    <StyledBodyCell>
                      <Typography>{transaction.formattedKind}</Typography>
                    </StyledBodyCell>
                    <StyledBodyCell>
                      <Typography align="right">
                        {renderAmount(transaction.amount)}
                      </Typography>
                    </StyledBodyCell>
                  </StyledTableRow>
                ))}
              </TableBody>
              <TableFooter sx={{position: 'sticky', bottom: 0}}>
                <StyledTableRow>
                  <StyledFooterCell colSpan={5} />
                  <StyledFooterCell>
                    <Stack spacing={0.5}>
                      <Box display="flex" gap={2}>
                        <Typography fontSize="12px" sx={{flex: 1}}>
                          Total:
                        </Typography>
                        <Typography fontSize="12px" sx={{textAlign: 'right'}}>
                          {renderAmount(totalAmount)}
                        </Typography>
                      </Box>
                    </Stack>
                  </StyledFooterCell>
                </StyledTableRow>
              </TableFooter>
            </Table>
          )}
        </StyledTableContainer>
      </Box>
    </>
  );
}

export default memo(UnpaidFixedAssetsPage);
